/**
 * Modal for scheduling a follow-up call from an open or recently-
 * wrapped interaction. Lives next to InteractionRecorder so the
 * agent can promise a callback in the same flow where they're
 * already typing `next_action_required`.
 *
 * Defaults:
 *   - `starts_at` defaults to tomorrow at 10:00 in the user's local
 *     timezone (sensible "I'll call you tomorrow morning" default)
 *   - `duration_minutes` defaults to 30 (matches the scheduling
 *     module's SLOT_DURATION_MIN)
 *   - `purpose` is pre-filled from the parent's `nextAction` field
 *
 * The submit calls POST /staff/interactions/{id}/schedule-callback
 * which atomically creates the appointment and (when the outcome
 * was still the placeholder) bumps the interaction's outcome to
 * `callback_scheduled`.
 */
import { useEffect, useMemo, useState } from 'react';

import Modal from '../Modal';
import interactionsService, {
  type ScheduledCallback,
} from '../../services/interactions.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import { tokens } from '../forms/tokens';

interface Props {
  open: boolean;
  interactionId: string;
  initialPurpose?: string;
  onClose: () => void;
  onScheduled: (appointment: ScheduledCallback) => void;
}

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '60 min' },
  { value: 90, label: '90 min' },
] as const;

/** Tomorrow at 10:00 local time, formatted for <input type="datetime-local">. */
function defaultStartsAtLocal(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  // datetime-local expects "YYYY-MM-DDTHH:MM" (no seconds, no timezone).
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** Turn a datetime-local string (no offset) into a tz-aware ISO
 *  string in the user's local zone — the backend requires tz-aware
 *  input. JS's Date constructor interprets the input as local time
 *  by default, and `.toISOString()` then converts to UTC with the Z
 *  suffix. */
function localInputToIso(local: string): string {
  return new Date(local).toISOString();
}

export default function ScheduleCallbackModal({
  open,
  interactionId,
  initialPurpose,
  onClose,
  onScheduled,
}: Readonly<Props>): JSX.Element {
  const initialStarts = useMemo(defaultStartsAtLocal, []);
  const [startsAt, setStartsAt] = useState(initialStarts);
  const [duration, setDuration] = useState<number>(30);
  const [purpose, setPurpose] = useState(initialPurpose ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // When the modal opens with a fresh `initialPurpose`, reflect it.
  useEffect(() => {
    if (open) {
      setPurpose(initialPurpose ?? '');
      setStartsAt(defaultStartsAtLocal());
      setDuration(30);
      setError(null);
    }
  }, [open, initialPurpose]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const appointment = await interactionsService.scheduleCallback(
        interactionId,
        {
          starts_at: localInputToIso(startsAt),
          duration_minutes: duration,
          purpose: purpose.trim() || undefined,
        },
      );
      onScheduled(appointment);
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not schedule the callback.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} busy={busy} maxWidth="md">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <header>
          <h3 style={{ margin: 0, fontSize: 16, color: tokens.textPrimary }}>
            Schedule callback
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: tokens.textMuted }}>
            Creates a real appointment for you and the client. The client will see
            it on their appointments list.
          </p>
        </header>

        {error ? (
          <div
            style={{
              background: colors.dangerBg,
              color: colors.danger,
              border: `1px solid ${colors.dangerBorder}`,
              borderRadius: 8,
              padding: 8,
              fontSize: 12,
            }}
          >
            {error}
          </div>
        ) : null}

        <label style={labelStyle}>
          When
          {/* */}
          <input
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            required
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          Duration
          {/* */}
          <select
            value={duration}
            onChange={(e) => setDuration(Number.parseInt(e.target.value, 10))}
            style={inputStyle}
          >
            {DURATION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <label style={labelStyle}>
          Purpose (visible on the appointment)
          {/* */}
          <input
            type="text"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            maxLength={500}
            style={inputStyle}
            placeholder="Follow up on a contribution, review their summary, etc."
          />
        </label>

        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={btnSecondary}
          >
            Cancel
          </button>
          <button type="submit" disabled={busy} style={btnPrimary}>
            {busy ? 'Scheduling…' : 'Schedule'}
          </button>
        </footer>
      </form>
    </Modal>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: tokens.textLabel,
};
const inputStyle: React.CSSProperties = {
  background: tokens.surfaceMuted,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  color: tokens.textPrimary,
  fontSize: 13,
};
const btnPrimary: React.CSSProperties = {
  background: tokens.gold,
  color: tokens.navy,
  border: 'none',
  borderRadius: 8,
  padding: '8px 14px',
  cursor: 'pointer',
  fontWeight: 600,
};
const btnSecondary: React.CSSProperties = {
  background: 'transparent',
  color: tokens.textPrimary,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 14px',
  cursor: 'pointer',
};
